import clsx from "clsx";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSelector } from "react-redux";
import { Events, dispatch } from "../../../redux/dispatch";

import { IoAlert } from "react-icons/io5";
import { IoCloseOutline } from "react-icons/io5";
import { TiWarning } from "react-icons/ti";
import { FaCheck } from "react-icons/fa";

const Alert: FC = () => {
  const {
    alert: { start, message, type },
  } = useSelector((state: any) => state.appControl);

  const [show, setShow] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finish = useCallback(() => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);

    setShow(false);
    hideTimerRef.current = setTimeout(() => {
      dispatch(Events.HIDE_ALERT);
      hideTimerRef.current = null;
    }, 700);
  }, []);

  useEffect(() => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);

    showTimerRef.current = setTimeout(() => {
      setShow(start);
      showTimerRef.current = null;
    }, 100);

    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
    };
  }, [start]);

  useEffect(() => {
    if (start) {
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);

      autoCloseTimerRef.current = setTimeout(() => {
        finish();
        autoCloseTimerRef.current = null;
      }, 3000);
    }

    return () => {
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    };
  }, [start, finish]);

  useEffect(
    () => () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    [],
  );

  const styles = useMemo(() => {
    if (type === "ERROR") return "bg-[#7A2E3A]/60 border-t-[#C84A5A]";
    if (type === "WARNING") return "bg-[#8A6A2E]/60 border-t-[#F2C94C]";
    if (type === "SUCCESS") return "bg-[#2F5D4A]/60 border-t-[#6FCF97]";
  }, [type]);

  const icon = useMemo(() => {
    if (type === "ERROR")
      return (
        <section className='bg-[#7A2E3A] p-[5px] rounded-full'>
          <IoAlert />
        </section>
      );
    if (type === "WARNING")
      return (
        <section>
          <TiWarning className='text-[#F2C94C] text-3xl' />
        </section>
      );
    if (type === "SUCCESS")
      return (
        <section className='bg-[#6FCF97] p-[5px] rounded-full'>
          <FaCheck />
        </section>
      );
  }, [type]);

  if (start)
    return (
      <section
        className={clsx(
          "flex absolute w-[90vw] backdrop-blur-md border-t-[2px] rounded-lg px-4",
          "py-3 text-white transition-all duration-500",
          "shadow-lg z-50 top-8",
          `${styles}`,
          {
            "opacity-0": !show,
            "opacity-100": show,
          },
        )}>
        {icon}
        <section className='flex-1 flex items-center'>
          <span className='text-[13px] px-3 text-white'>{message ?? ""}</span>
        </section>
        <section className='flex items-center'>
          <IoCloseOutline
            className='text-[20px] text-white/70'
            onClick={finish}
          />
        </section>
      </section>
    );

  return <></>;
};

export default Alert;
